/**
 * MandantClarificationsInbox — Inbox-Sektion in MandantCaseHome.
 *
 * Zwei Listen:
 *   1. „Nachrichten vom Berater" — Berater hat eine Frage gestellt, der
 *      Mandant muss antworten (created_by_role='berater' AND
 *      answered_at IS NULL).
 *   2. „Ihre offenen Fragen" — Der Mandant hat eine Frage gestellt und
 *      wartet auf Antwort/Auflösung des Beraters.
 *
 * Wenn beide Listen leer sind, rendert die Komponente NICHTS — keine
 * unnötige Empty-State-Karte im Mandanten-Dashboard.
 *
 * Self-Answer ist DB-seitig blockiert (Trigger 0017). Wir zeigen den
 * Antwort-Button bei Mandant-eigenen Fragen daher gar nicht.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { de } from '../../lib/i18n/de';
import {
  answerClarification,
  listClarificationsForCase,
  type CaseClarification,
} from '../../lib/supabaseClarifications';
import { useAuthOrSession } from '../../lib/authOrSession';

interface MandantClarificationsInboxProps {
  readonly caseId: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: ReadonlyArray<CaseClarification> }
  | { kind: 'error'; message: string };

export function MandantClarificationsInbox({ caseId }: MandantClarificationsInboxProps) {
  const { isMock } = useAuthOrSession();
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });

  const refresh = useCallback(async () => {
    if (isMock) {
      // Demo-Modus: nichts laden — die Inbox bleibt leer.
      setLoad({ kind: 'ready', items: [] });
      return;
    }
    setLoad({ kind: 'loading' });
    try {
      const items = await listClarificationsForCase(caseId);
      setLoad({ kind: 'ready', items });
    } catch (err) {
      setLoad({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Konnte Rückfragen nicht laden.',
      });
    }
  }, [caseId, isMock]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const beraterQuestions = useMemo(() => {
    if (load.kind !== 'ready') return [];
    // Berater fragt, Mandant antwortet noch nicht
    return load.items.filter(
      (c) => c.created_by_role === 'berater' && c.answered_at === null && c.resolved_at === null,
    );
  }, [load]);

  const myPending = useMemo(() => {
    if (load.kind !== 'ready') return [];
    // Mandant hat gefragt, Berater hat noch nicht resolved
    return load.items.filter((c) => c.created_by_role === 'mandant' && c.resolved_at === null);
  }, [load]);

  if (load.kind === 'loading') return null;
  if (load.kind === 'error') {
    return (
      <section className="mt-10 rounded-sm border border-amber-200 bg-amber-50 px-5 py-4">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-amber-800">
          {de.mandant.clarifications.sectionLabel}
        </p>
        <p className="mt-1 font-serif text-sm text-amber-900">{load.message}</p>
      </section>
    );
  }

  if (beraterQuestions.length === 0 && myPending.length === 0) {
    return null;
  }

  return (
    <section
      className="mt-10 border-t border-stone-200 pt-8"
      aria-label={de.mandant.clarifications.sectionLabel}
    >
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.mandant.clarifications.sectionLabel}
      </p>

      {beraterQuestions.length > 0 && (
        <div className="mt-3">
          <h2 className="font-serif text-2xl font-bold tracking-tight text-stone-900">
            {de.mandant.clarifications.sectionTitleInbox}
          </h2>
          <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-500">
            {de.mandant.clarifications.sectionInboxIntro}
          </p>
          <ul className="mt-5 space-y-3">
            {beraterQuestions.map((c) => (
              <BeraterQuestionItem
                key={c.id}
                clarification={c}
                onAnswered={() => {
                  void refresh();
                }}
              />
            ))}
          </ul>
        </div>
      )}

      {myPending.length > 0 && (
        <div className={beraterQuestions.length > 0 ? 'mt-10' : 'mt-3'}>
          <h3 className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.mandant.clarifications.sectionPendingTitle}
          </h3>
          <p className="mt-1 max-w-prose font-serif text-sm italic text-stone-500">
            {de.mandant.clarifications.sectionPendingIntro}
          </p>
          <ul className="mt-3 space-y-2">
            {myPending.map((c) => (
              <MyPendingItem key={c.id} clarification={c} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Items
// -----------------------------------------------------------------------------

function BeraterQuestionItem({
  clarification,
  onAnswered,
}: {
  clarification: CaseClarification;
  onAnswered: () => void;
}) {
  const [answer, setAnswer] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      await answerClarification(clarification.id, answer);
      onAnswered();
    } catch (err) {
      setError(err instanceof Error ? err.message : de.mandant.clarifications.sendError);
    } finally {
      setSending(false);
    }
  };

  return (
    <li className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-4 md:px-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-amber-800">
            {de.mandant.clarifications.askedByBerater} ·{' '}
            {de.mandant.clarifications.askedAt.replace(
              '{date}',
              new Date(clarification.created_at).toLocaleDateString('de-DE'),
            )}
          </p>
          {clarification.field_label && (
            <p className="mt-0.5 font-sans text-[11px] uppercase tracking-widest text-amber-700">
              {de.mandant.clarifications.onField}: {clarification.field_label}
            </p>
          )}
          <p className="mt-2 font-serif text-base leading-relaxed text-amber-950">
            {clarification.question}
          </p>
        </div>
      </div>
      <div className="mt-4">
        <label className="font-sans text-xs font-medium uppercase tracking-widest text-amber-900">
          {de.mandant.clarifications.answerLabel}
        </label>
        <textarea
          value={answer}
          onChange={(e) => {
            setAnswer(e.target.value);
          }}
          placeholder={de.mandant.clarifications.answerPlaceholder}
          rows={3}
          className="mt-2 w-full rounded-sm border border-stone-300 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
        />
        <div className="mt-3 flex flex-col items-stretch justify-end gap-2 sm:flex-row sm:items-center">
          {error && <p className="font-serif text-sm text-rose-700 sm:flex-1">{error}</p>}
          <button
            type="button"
            disabled={sending || answer.trim().length === 0}
            onClick={() => {
              void submit();
            }}
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-stone-900 px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-white hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-300"
          >
            {sending ? de.mandant.clarifications.sending : de.mandant.clarifications.sendAnswer}
          </button>
        </div>
      </div>
    </li>
  );
}

function MyPendingItem({ clarification }: { clarification: CaseClarification }) {
  return (
    <li className="rounded-sm border border-stone-200 bg-stone-50 px-4 py-3">
      <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-stone-500">
        {de.mandant.clarifications.askedByMe} ·{' '}
        {de.mandant.clarifications.askedAt.replace(
          '{date}',
          new Date(clarification.created_at).toLocaleDateString('de-DE'),
        )}
      </p>
      {clarification.field_label && (
        <p className="mt-0.5 font-sans text-[11px] uppercase tracking-widest text-stone-500">
          {de.mandant.clarifications.onField}: {clarification.field_label}
        </p>
      )}
      <p className="mt-1 font-serif text-sm text-stone-800">{clarification.question}</p>
      {clarification.answered_at && clarification.answer && (
        <div className="mt-3 rounded-sm border-l-2 border-emerald-300 bg-white px-3 py-2">
          <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-emerald-700">
            {de.mandant.clarifications.answeredAt.replace(
              '{date}',
              new Date(clarification.answered_at).toLocaleDateString('de-DE'),
            )}
          </p>
          <p className="mt-1 font-serif text-sm text-stone-800">{clarification.answer}</p>
        </div>
      )}
    </li>
  );
}
